import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "motion/react";
import {
  Phone,
  Instagram,
  Star,
  MapPin,
  Heart,
  Utensils,
  Music,
  ChevronDown,
  ArrowRight,
  CheckCircle2,
  Quote,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface TestimonialItem {
  name: string;
  event: string;
  text: string;
  stars: number;
}

interface ServiceCard {
  icon: React.ReactNode;
  title: string;
  description: string;
  tag: string;
}

// ─── Fade-up animation helper ─────────────────────────────────────────────────

function FadeUp({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// ─── Navbar ───────────────────────────────────────────────────────────────────

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: "Servicios", href: "#servicios" },
    { label: "Nosotros", href: "#nosotros" },
    { label: "Testimonios", href: "#testimonios" },
    { label: "Contacto", href: "#contacto" },
  ];

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        background: scrolled
          ? "rgba(10,2,4,0.92)"
          : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(192,72,96,0.15)" : "none",
      }}
    >
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <a href="#" aria-label="El Champiñón Dorado — inicio">
          <img
            src="https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/94b84552-6f77-42da-b9bd-c67baa4fe617-318092699_195251742988853_376924781438301035_n.jpg"
            alt="El Champiñón Dorado logo"
            className="h-10 w-10 rounded-full object-cover ring-2 ring-[#c09048]"
          />
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="text-sm transition-colors duration-200"
              style={{
                fontFamily: "'Barlow', sans-serif",
                color: "rgba(255,240,230,0.72)",
                letterSpacing: "0.03em",
              }}
              onMouseEnter={(e) =>
                ((e.target as HTMLAnchorElement).style.color = "#c09048")
              }
              onMouseLeave={(e) =>
                ((e.target as HTMLAnchorElement).style.color =
                  "rgba(255,240,230,0.72)")
              }
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* CTA */}
        <a
          href="https://wa.me/573001234567?text=Hola%2C%20quiero%20cotizar%20un%20evento"
          target="_blank"
          rel="noopener noreferrer"
          className="hidden md:flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200"
          style={{
            background: "#c01830",
            color: "#fff5f0",
            fontFamily: "'Barlow', sans-serif",
          }}
          onMouseEnter={(e) =>
            ((e.currentTarget as HTMLAnchorElement).style.background = "#a01228")
          }
          onMouseLeave={(e) =>
            ((e.currentTarget as HTMLAnchorElement).style.background = "#c01830")
          }
        >
          <Phone size={15} />
          Cotizar evento
        </a>

        {/* Mobile menu toggle */}
        <button
          className="md:hidden p-2 rounded"
          style={{ color: "rgba(255,240,230,0.8)" }}
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Abrir menú"
        >
          <div className="space-y-1">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="block w-6 h-0.5 transition-all"
                style={{ background: "#c09048" }}
              />
            ))}
          </div>
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            style={{ background: "rgba(10,2,4,0.97)", overflow: "hidden" }}
          >
            <div className="px-6 pb-6 flex flex-col gap-4 pt-2">
              {links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setMenuOpen(false)}
                  className="text-base py-1"
                  style={{
                    fontFamily: "'Barlow', sans-serif",
                    color: "rgba(255,240,230,0.8)",
                    letterSpacing: "0.03em",
                  }}
                >
                  {l.label}
                </a>
              ))}
              <a
                href="https://wa.me/573001234567"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium"
                style={{ background: "#c01830", color: "#fff" }}
              >
                <Phone size={15} />
                Cotizar evento
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: "#080103" }}
    >
      {/* Decorative background shapes */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
      >
        {/* Background photo */}
        <img
          src="https://pixabay.com/get/g01b3d1f038afae9d50d689d5ea24ea28d74b3ce47e483474fb3c332503938d33270998db5be61d094e5f2c83dcc347de5332118ab85485e649d7561a6c1d6eb4_1280.jpg"
          alt="Mesa de catering elegantemente decorada para evento"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.22 }}
        />
        {/* Dark scrim over photo */}
        <div className="absolute inset-0" style={{ background: "rgba(8,1,3,0.55)" }} />
        {/* Warm crimson glow — top right */}
        <div
          style={{
            position: "absolute",
            top: "-10%",
            right: "-5%",
            width: 640,
            height: 640,
            background:
              "radial-gradient(circle, rgba(192,24,48,0.22) 0%, transparent 70%)",
            filter: "blur(80px)",
          }}
        />
        {/* Gold glow — bottom left */}
        <div
          style={{
            position: "absolute",
            bottom: "-5%",
            left: "-5%",
            width: 520,
            height: 520,
            background:
              "radial-gradient(circle, rgba(192,144,72,0.18) 0%, transparent 70%)",
            filter: "blur(100px)",
          }}
        />
        {/* Fine dot grid */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle, rgba(192,144,72,0.07) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        {/* Thin decorative lines */}
        <svg
          className="absolute bottom-0 left-0 w-full"
          viewBox="0 0 1440 80"
          fill="none"
          preserveAspectRatio="none"
          style={{ opacity: 0.25 }}
        >
          <path
            d="M0 80 Q360 0 720 40 Q1080 80 1440 20 L1440 80 Z"
            fill="rgba(192,144,72,0.3)"
          />
        </svg>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-3xl">
          {/* Pre-headline tag */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <span
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-8"
              style={{
                background: "rgba(192,144,72,0.12)",
                color: "#c09048",
                border: "1px solid rgba(192,144,72,0.3)",
                fontFamily: "'Barlow', sans-serif",
              }}
            >
              <MapPin size={11} />
              Cali, Colombia
            </span>
          </motion.div>

          {/* Main headline */}
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            style={{
              fontFamily: "'Bodoni Moda', serif",
              fontSize: "clamp(3rem, 8vw, 6.5rem)",
              lineHeight: 1.0,
              color: "#fff5f0",
              letterSpacing: "-0.02em",
              fontWeight: 700,
            }}
          >
            Momentos que{" "}
            <em
              style={{
                fontStyle: "italic",
                color: "#c09048",
              }}
            >
              perduran
            </em>
            <br />
            para siempre.
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 mb-10 max-w-xl"
            style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize: "clamp(1rem, 2vw, 1.15rem)",
              lineHeight: 1.75,
              color: "rgba(255,240,230,0.68)",
            }}
          >
            Catering y producción de eventos para bodas, quinceañeras, fiestas y
            celebraciones especiales. Cada detalle, cada sabor, cada momento —
            diseñado para ti.
          </motion.p>

          {/* CTA row */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.44, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col sm:flex-row gap-4 items-start"
          >
            {/* Primary CTA */}
            <a
              href="https://wa.me/573001234567?text=Hola%2C%20quiero%20cotizar%20mi%20evento"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 pl-6 pr-3 py-3 rounded-full transition-all duration-200"
              style={{
                background: "#c01830",
                color: "#fff",
                fontFamily: "'Barlow', sans-serif",
                fontSize: "1rem",
                fontWeight: 600,
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLAnchorElement).style.background = "#a01228")
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLAnchorElement).style.background = "#c01830")
              }
            >
              Cotizar mi evento
              <span
                className="inline-flex items-center justify-center w-9 h-9 rounded-full transition-transform duration-200 group-hover:translate-x-0.5"
                style={{ background: "rgba(255,255,255,0.18)" }}
              >
                <ArrowRight size={17} />
              </span>
            </a>

            {/* Secondary CTA */}
            <a
              href="https://www.instagram.com/elchampinnondorado/"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 px-5 py-3 rounded-full transition-all duration-200"
              style={{
                color: "rgba(255,240,230,0.75)",
                fontFamily: "'Barlow', sans-serif",
                fontSize: "1rem",
                border: "1px solid rgba(192,144,72,0.3)",
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLAnchorElement;
                el.style.color = "#c09048";
                el.style.borderColor = "rgba(192,144,72,0.6)";
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLAnchorElement;
                el.style.color = "rgba(255,240,230,0.75)";
                el.style.borderColor = "rgba(192,144,72,0.3)";
              }}
            >
              <Instagram size={16} />
              Ver galería
            </a>
          </motion.div>
        </div>

        {/* Floating stat pills */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-20 flex flex-wrap gap-4"
        >
          {[
            { value: "+500", label: "eventos realizados" },
            { value: "10+", label: "años de experiencia" },
            { value: "100%", label: "clientes satisfechos" },
          ].map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-3 px-5 py-3 rounded-2xl"
              style={{
                background: "rgba(192,24,48,0.08)",
                border: "1px solid rgba(192,72,96,0.2)",
              }}
            >
              <span
                style={{
                  fontFamily: "'Bodoni Moda', serif",
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  color: "#c09048",
                  lineHeight: 1,
                }}
              >
                {stat.value}
              </span>
              <span
                style={{
                  fontFamily: "'Barlow', sans-serif",
                  fontSize: "0.8rem",
                  color: "rgba(255,240,230,0.55)",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                {stat.label}
              </span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <ChevronDown size={22} style={{ color: "rgba(192,144,72,0.5)" }} />
      </motion.div>
    </section>
  );
}

// ─── Services ─────────────────────────────────────────────────────────────────

const services: ServiceCard[] = [
  {
    icon: <Heart size={26} />,
    title: "Quinceañeras",
    description:
      "El día más especial de tu hija merece el mejor servicio. Decoración, catering y coordinación completa para una celebración inolvidable.",
    tag: "Celebración",
  },
  {
    icon: <Star size={26} />,
    title: "Bodas",
    description:
      "Menús personalizados, montajes elegantes y atención impecable para que tu boda sea exactamente como la soñaste.",
    tag: "Matrimonios",
  },
  {
    icon: <Music size={26} />,
    title: "Fiestas & Reuniones",
    description:
      "Desde cumpleaños íntimos hasta fiestas corporativas. Adaptamos el servicio a tu presupuesto y estilo.",
    tag: "Eventos sociales",
  },
  {
    icon: <Utensils size={26} />,
    title: "Eventos Especiales",
    description:
      "Lanzamientos, aniversarios, graduaciones y más. Nos encargamos de la producción completa para que tú solo disfrutes.",
    tag: "Corporativo & más",
  },
];

function Services() {
  return (
    <section id="servicios" className="py-24 md:py-36" style={{ background: "#0a0204" }}>
      <div className="max-w-6xl mx-auto px-6">
        <FadeUp>
          <span
            className="text-xs font-semibold uppercase tracking-widest"
            style={{
              fontFamily: "'Barlow', sans-serif",
              color: "#c09048",
              letterSpacing: "0.12em",
            }}
          >
            Lo que hacemos
          </span>
        </FadeUp>

        <FadeUp delay={0.08}>
          <h2
            className="mt-4 mb-16"
            style={{
              fontFamily: "'Bodoni Moda', serif",
              fontSize: "clamp(2rem, 5vw, 3.5rem)",
              lineHeight: 1.1,
              color: "#fff5f0",
              maxWidth: "28ch",
            }}
          >
            Cada evento, una experiencia{" "}
            <em style={{ color: "#c09048", fontStyle: "italic" }}>única</em>
          </h2>
        </FadeUp>

        <div className="grid md:grid-cols-2 gap-6">
          {services.map((svc, i) => (
            <FadeUp key={svc.title} delay={0.1 + i * 0.09}>
              <div
                className="group relative p-8 rounded-2xl h-full transition-all duration-300"
                style={{
                  background: "rgba(192,24,48,0.05)",
                  border: "1px solid rgba(192,72,96,0.15)",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.background =
                    "rgba(192,24,48,0.1)";
                  (e.currentTarget as HTMLDivElement).style.borderColor =
                    "rgba(192,144,72,0.3)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.background =
                    "rgba(192,24,48,0.05)";
                  (e.currentTarget as HTMLDivElement).style.borderColor =
                    "rgba(192,72,96,0.15)";
                }}
              >
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="p-3 rounded-xl"
                    style={{
                      background: "rgba(192,144,72,0.12)",
                      color: "#c09048",
                    }}
                  >
                    {svc.icon}
                  </div>
                  <span
                    className="text-xs font-medium px-3 py-1 rounded-full"
                    style={{
                      background: "rgba(192,24,48,0.15)",
                      color: "#c04860",
                      fontFamily: "'Barlow', sans-serif",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {svc.tag}
                  </span>
                </div>

                <h3
                  className="mb-3"
                  style={{
                    fontFamily: "'Bodoni Moda', serif",
                    fontSize: "1.45rem",
                    fontWeight: 700,
                    color: "#fff5f0",
                    lineHeight: 1.2,
                  }}
                >
                  {svc.title}
                </h3>
                <p
                  style={{
                    fontFamily: "'Barlow', sans-serif",
                    fontSize: "0.97rem",
                    lineHeight: 1.7,
                    color: "rgba(255,240,230,0.62)",
                  }}
                >
                  {svc.description}
                </p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Photo Strip ──────────────────────────────────────────────────────────────

function PhotoStrip() {
  return (
    <div className="overflow-hidden" style={{ background: "#080103" }}>
      <div className="grid grid-cols-2 gap-1">
        <div className="relative overflow-hidden" style={{ height: "clamp(200px, 30vw, 380px)" }}>
          <img
            src="https://pixabay.com/get/gb85e75ffdbc85329c20c7d43ad1e82e654f11613c76057620c567655d78e07340ff3eb354a202a36780d591b1b413ed4333db1311154b12d06bc5b5db5182b58_1280.jpg"
            alt="Decoración y montaje para evento de bodas en Cali"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
          />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(to top, rgba(8,1,3,0.5) 0%, transparent 50%)" }}
          />
          <span
            className="absolute bottom-4 left-4 text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full"
            style={{
              background: "rgba(192,24,48,0.75)",
              color: "#fff5f0",
              fontFamily: "'Barlow', sans-serif",
              backdropFilter: "blur(6px)",
            }}
          >
            Bodas
          </span>
        </div>
        <div className="relative overflow-hidden" style={{ height: "clamp(200px, 30vw, 380px)" }}>
          <img
            src="https://pixabay.com/get/gfafc268ec118235a3a7c2d0ead7a3cb067fec6ee16903201ec1fa40767e88335114bfa1093176c2703e39602dc3f958db2ae2780b953bc929bed78d9af3cd1b2_1280.jpg"
            alt="Presentación gastronómica para quinceañera y eventos especiales"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
          />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(to top, rgba(8,1,3,0.5) 0%, transparent 50%)" }}
          />
          <span
            className="absolute bottom-4 left-4 text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full"
            style={{
              background: "rgba(192,144,72,0.75)",
              color: "#fff5f0",
              fontFamily: "'Barlow', sans-serif",
              backdropFilter: "blur(6px)",
            }}
          >
            Quinceañeras
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Why Us ───────────────────────────────────────────────────────────────────

const perks = [
  "Menús personalizados para cada celebración",
  "Equipo profesional con más de 10 años de experiencia",
  "Montaje, servicio y limpieza incluidos",
  "Asesoría gratuita para planear tu evento",
  "Opciones para todos los presupuestos",
  "Puntualidad y atención al detalle garantizadas",
];

function WhyUs() {
  return (
    <section id="nosotros" className="py-24 md:py-36" style={{ background: "#06010208" }}>
      <div
        className="py-24 md:py-32"
        style={{
          background:
            "linear-gradient(135deg, rgba(192,24,48,0.08) 0%, rgba(192,144,72,0.05) 100%)",
          borderTop: "1px solid rgba(192,72,96,0.12)",
          borderBottom: "1px solid rgba(192,72,96,0.12)",
        }}
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Left copy */}
            <div>
              <FadeUp>
                <span
                  className="text-xs font-semibold uppercase tracking-widest"
                  style={{
                    fontFamily: "'Barlow', sans-serif",
                    color: "#c09048",
                    letterSpacing: "0.12em",
                  }}
                >
                  Por qué elegirnos
                </span>
              </FadeUp>
              <FadeUp delay={0.08}>
                <h2
                  className="mt-4 mb-6"
                  style={{
                    fontFamily: "'Bodoni Moda', serif",
                    fontSize: "clamp(1.9rem, 4vw, 3rem)",
                    lineHeight: 1.15,
                    color: "#fff5f0",
                  }}
                >
                  Sabor, elegancia y{" "}
                  <em style={{ color: "#c09048", fontStyle: "italic" }}>
                    dedicación
                  </em>{" "}
                  en cada plato.
                </h2>
              </FadeUp>
              <FadeUp delay={0.14}>
                <p
                  style={{
                    fontFamily: "'Barlow', sans-serif",
                    fontSize: "1rem",
                    lineHeight: 1.75,
                    color: "rgba(255,240,230,0.62)",
                    maxWidth: "50ch",
                  }}
                >
                  Somos un equipo apasionado por la gastronomía y los eventos en
                  Cali. Combinamos ingredientes frescos, recetas auténticas y un
                  servicio cálido para crear celebraciones que la gente recuerda
                  por años.
                </p>
              </FadeUp>
            </div>

            {/* Right column: photo + checklist */}
            <div className="flex flex-col gap-8">
              <FadeUp>
                <div className="rounded-2xl overflow-hidden" style={{ maxHeight: 260 }}>
                  <img
                    src="https://pixabay.com/get/gbc89154e61c63e82396e6c7d5f187f0ea0230d123b90cddb9afcd8e2e3f051d59faf7b4be51c3fe2ed7d883dd4dc58727c92bd4da7644ec4cee42ecd139548b7_1280.jpg"
                    alt="Chef preparando platos para un evento de catering en Cali"
                    className="w-full h-full object-cover"
                    style={{ maxHeight: 260 }}
                  />
                </div>
              </FadeUp>
            <div className="grid gap-4">
              {perks.map((perk, i) => (
                <FadeUp key={perk} delay={0.06 + i * 0.07}>
                  <div className="flex items-start gap-3">
                    <CheckCircle2
                      size={19}
                      className="shrink-0 mt-0.5"
                      style={{ color: "#c09048" }}
                    />
                    <span
                      style={{
                        fontFamily: "'Barlow', sans-serif",
                        fontSize: "0.97rem",
                        lineHeight: 1.5,
                        color: "rgba(255,240,230,0.78)",
                      }}
                    >
                      {perk}
                    </span>
                  </div>
                </FadeUp>
              ))}
            </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Testimonials ─────────────────────────────────────────────────────────────

const testimonials: TestimonialItem[] = [
  {
    name: "María Fernanda Ríos",
    event: "Quinceañera — Cali",
    text: "El Champiñón Dorado hizo el sueño de mi hija realidad. La comida estaba deliciosa y el servicio fue impecable. ¡Todos los invitados quedaron encantados!",
    stars: 5,
  },
  {
    name: "Andrés & Juliana Mora",
    event: "Boda — Cali",
    text: "Desde la primera reunión hasta el último bocado, todo fue perfecto. Nos dieron tranquilidad total para que pudiéramos disfrutar nuestro día sin preocupaciones.",
    stars: 5,
  },
  {
    name: "Carolina Vargas",
    event: "Evento Corporativo",
    text: "Profesionalismo total. Llegaron a tiempo, el montaje fue elegante y la comida superó todas las expectativas. Los recomiendo sin dudarlo.",
    stars: 5,
  },
];

function Testimonials() {
  return (
    <section
      id="testimonios"
      className="py-24 md:py-36"
      style={{ background: "#080103" }}
    >
      <div className="max-w-6xl mx-auto px-6">
        <FadeUp>
          <span
            className="text-xs font-semibold uppercase tracking-widest"
            style={{
              fontFamily: "'Barlow', sans-serif",
              color: "#c09048",
              letterSpacing: "0.12em",
            }}
          >
            Lo que dicen nuestros clientes
          </span>
        </FadeUp>

        <FadeUp delay={0.08}>
          <h2
            className="mt-4 mb-16"
            style={{
              fontFamily: "'Bodoni Moda', serif",
              fontSize: "clamp(1.9rem, 4vw, 3rem)",
              lineHeight: 1.1,
              color: "#fff5f0",
            }}
          >
            Historias que nos llenan de{" "}
            <em style={{ color: "#c09048", fontStyle: "italic" }}>orgullo</em>
          </h2>
        </FadeUp>

        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <FadeUp key={t.name} delay={0.1 + i * 0.1}>
              <article
                className="p-7 rounded-2xl h-full flex flex-col"
                style={{
                  background: "rgba(192,24,48,0.06)",
                  border: "1px solid rgba(192,72,96,0.15)",
                }}
              >
                <Quote
                  size={28}
                  className="mb-5"
                  style={{ color: "rgba(192,144,72,0.35)" }}
                />
                <p
                  className="flex-1 mb-6"
                  style={{
                    fontFamily: "'Barlow', sans-serif",
                    fontSize: "0.95rem",
                    lineHeight: 1.75,
                    color: "rgba(255,240,230,0.72)",
                  }}
                >
                  "{t.text}"
                </p>

                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: t.stars }).map((_, si) => (
                    <Star
                      key={si}
                      size={14}
                      fill="#c09048"
                      style={{ color: "#c09048" }}
                    />
                  ))}
                </div>

                <div>
                  <p
                    style={{
                      fontFamily: "'Bodoni Moda', serif",
                      fontSize: "1rem",
                      fontWeight: 700,
                      color: "#fff5f0",
                      lineHeight: 1.3,
                    }}
                  >
                    {t.name}
                  </p>
                  <p
                    style={{
                      fontFamily: "'Barlow', sans-serif",
                      fontSize: "0.8rem",
                      color: "#c04860",
                      letterSpacing: "0.04em",
                      marginTop: 2,
                    }}
                  >
                    {t.event}
                  </p>
                </div>
              </article>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── CTA Section ──────────────────────────────────────────────────────────────

function CtaSection() {
  return (
    <section
      id="contacto"
      className="py-24 md:py-36 relative overflow-hidden"
      style={{ background: "#0a0204" }}
    >
      {/* Background glow */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          width: 700,
          height: 400,
          background:
            "radial-gradient(ellipse, rgba(192,24,48,0.16) 0%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      <div className="relative max-w-3xl mx-auto px-6 text-center">
        <FadeUp>
          <h2
            className="mb-5"
            style={{
              fontFamily: "'Bodoni Moda', serif",
              fontSize: "clamp(2.2rem, 6vw, 4rem)",
              lineHeight: 1.08,
              color: "#fff5f0",
            }}
          >
            ¿Listo para crear un evento{" "}
            <em style={{ color: "#c09048", fontStyle: "italic" }}>
              inolvidable?
            </em>
          </h2>
        </FadeUp>

        <FadeUp delay={0.1}>
          <p
            className="mb-10 mx-auto"
            style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize: "1.05rem",
              lineHeight: 1.72,
              color: "rgba(255,240,230,0.62)",
              maxWidth: "50ch",
            }}
          >
            Cuéntanos sobre tu celebración y recibe una cotización personalizada.
            Estamos en Cali y listos para hacer que tu evento sea perfecto.
          </p>
        </FadeUp>

        <FadeUp delay={0.18}>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://wa.me/573001234567?text=Hola%2C%20quiero%20cotizar%20mi%20evento"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 pl-6 pr-3 py-3.5 rounded-full transition-all duration-200"
              style={{
                background: "#c01830",
                color: "#fff",
                fontFamily: "'Barlow', sans-serif",
                fontSize: "1rem",
                fontWeight: 600,
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLAnchorElement).style.background = "#a01228")
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLAnchorElement).style.background = "#c01830")
              }
            >
              <Phone size={17} />
              Escribir por WhatsApp
              <span
                className="inline-flex items-center justify-center w-9 h-9 rounded-full"
                style={{ background: "rgba(255,255,255,0.18)" }}
              >
                <ArrowRight size={17} />
              </span>
            </a>

            <a
              href="https://www.instagram.com/elchampinnondorado/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full transition-all duration-200"
              style={{
                color: "rgba(255,240,230,0.75)",
                fontFamily: "'Barlow', sans-serif",
                fontSize: "1rem",
                border: "1px solid rgba(192,144,72,0.35)",
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLAnchorElement;
                el.style.color = "#c09048";
                el.style.borderColor = "rgba(192,144,72,0.7)";
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLAnchorElement;
                el.style.color = "rgba(255,240,230,0.75)";
                el.style.borderColor = "rgba(192,144,72,0.35)";
              }}
            >
              <Instagram size={17} />
              Seguir en Instagram
            </a>
          </div>
        </FadeUp>

        <FadeUp delay={0.26}>
          <p
            className="mt-8 flex items-center justify-center gap-1.5"
            style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize: "0.82rem",
              color: "rgba(255,240,230,0.38)",
            }}
          >
            <MapPin size={13} style={{ color: "#c04860" }} />
            Cali, Valle del Cauca — Colombia
          </p>
        </FadeUp>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer
      className="py-8"
      style={{
        background: "#060102",
        borderTop: "1px solid rgba(192,72,96,0.12)",
      }}
    >
      <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img
            src="https://plada-uploads.s3.us-east-1.amazonaws.com/builder-uploads/landing-pages/94b84552-6f77-42da-b9bd-c67baa4fe617-318092699_195251742988853_376924781438301035_n.jpg"
            alt="El Champiñón Dorado"
            className="h-7 w-7 rounded-full object-cover"
          />
          <span
            style={{
              fontFamily: "'Bodoni Moda', serif",
              fontSize: "0.9rem",
              color: "rgba(255,240,230,0.5)",
            }}
          >
            El Champiñón Dorado
          </span>
        </div>

        <div className="flex items-center gap-6">
          <a
            href="https://www.instagram.com/elchampinnondorado/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram de El Champiñón Dorado"
          >
            <Instagram
              size={18}
              style={{ color: "rgba(255,240,230,0.35)" }}
              onMouseEnter={(e) =>
                ((e.currentTarget as SVGElement).style.color = "#c09048")
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as SVGElement).style.color =
                  "rgba(255,240,230,0.35)")
              }
            />
          </a>
        </div>

        <p
          className="plada-footer-mark text-xs"
          style={{
            fontFamily: "'Barlow', sans-serif",
            color: "rgba(255,240,230,0.28)",
          }}
        >
          Built with Plada
        </p>
      </div>
    </footer>
  );
}

// ─── Root Component ───────────────────────────────────────────────────────────

export default function BuilderTest142() {
  // Load Bodoni Moda font
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Bodoni+Moda:ital,wght@0,600;0,700;1,600;1,700&family=Barlow:wght@400;500;600&display=swap";
    document.head.appendChild(link);
    return () => {
      if (link.parentNode) link.parentNode.removeChild(link);
    };
  }, []);

  return (
    <div style={{ background: "#080103", minHeight: "100vh" }}>
      <Navbar />
      <main>
        <Hero />
        <Services />
        <PhotoStrip />
        <WhyUs />
        <Testimonials />
        <CtaSection />
      </main>
      <Footer />
    </div>
  );
}
